import { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { useKit } from './hooks/useKit';
import { Header, QueryInput, LoadingState, ErrorState, OfflineIndicator, InstallPrompt, HeroCarousel, StickyCTA, AdventureImageGallery, type QuerySubmitOptions } from './components';
import { HowWePick, About, Privacy, SavedKit, Routes as RoutesPage, RoutePage, Login, AuthVerify, GearCloset, Category, Shakedown, SavedShakedown, History, BestFor, BestForIndex, Comparison } from './pages';
import { AuthProvider } from './contexts/AuthContext';
import './App.css';

const queryClient = new QueryClient();

interface LocationState {
  prefillQuery?: string;
}

function HomePage() {
  const kitMutation = useKit();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const initialQuery = locationState?.prefillQuery || '';
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(initialQuery);
  const heroRef = useRef<HTMLDivElement>(null);

  // Handle scroll to show/hide sticky CTA
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowStickyCTA(heroBottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = useCallback((options: QuerySubmitOptions) => {
    kitMutation.mutate(
      {
        query: options.query,
        fit: options.fit,
        budget_tier: options.budget_tier,
        conditions: options.conditions,
      },
      {
        onSuccess: (data) => {
          // Navigate to SEO-friendly slug URL with kit data
          if (data.savedSlug) {
            navigate(`/kit/${data.savedSlug}`, {
              state: { kitData: data },
            });
          }
        },
      }
    );
  }, [kitMutation, navigate]);

  const handleStickyCTAClick = useCallback(() => {
    // Scroll to input
    const input = document.querySelector('textarea');
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleRetry = () => {
    kitMutation.reset();
  };

  const handleGalleryRouteClick = useCallback((query: string) => {
    setSelectedQuery(query);
    // Focus the input after selecting a route
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>('input[placeholder*="adventure"]');
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  return (
    <main className="flex-1">
      {/* Hero Section with Carousel Background */}
      {!kitMutation.isPending && !kitMutation.isError && (
        <div ref={heroRef} className="relative">
          {/* Hero Carousel Background */}
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <HeroCarousel paused={kitMutation.isPending} />
          </div>

          {/* Hero Content Overlay */}
          <div className="relative z-10 px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
            <div className="max-w-4xl mx-auto text-center animate-fadeIn">
              <h1 className="font-mono text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
                Tell us what you're doing.
                <br />
                <span className="text-orange-400">We'll tell you what to buy.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-100 max-w-xl mx-auto mb-8 drop-shadow-md">
                No quizzes. No "top 10" lists. No sponsored bullshit.
                <br className="hidden sm:block" />{' '}
                Just the gear you actually need.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Adventure Gallery - Slow scrolling route images */}
      {!kitMutation.isPending && !kitMutation.isError && (
        <div className="overflow-hidden">
          <AdventureImageGallery
            onRouteClick={handleGalleryRouteClick}
            scrollDuration={40}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="px-4 sm:px-6 py-8 sm:py-12">
        {/* Query Input */}
        {!kitMutation.isPending && !kitMutation.isError && (
          <QueryInput
            onSubmit={handleSubmit}
            isLoading={kitMutation.isPending}
            initialQuery={selectedQuery}
          />
        )}

        {/* Loading State */}
        {kitMutation.isPending && <LoadingState />}

        {/* Error State */}
        {kitMutation.isError && (
          <ErrorState
            message={kitMutation.error?.message || 'Failed to get kit recommendations'}
            onRetry={handleRetry}
          />
        )}
      </div>

      {/* Sticky Mobile CTA */}
      {!kitMutation.isPending && !kitMutation.isError && (
        <StickyCTA visible={showStickyCTA} onClick={handleStickyCTAClick}>
          Plan Your Kit
        </StickyCTA>
      )}
    </main>
  );
}

function Footer() {
  return (
    <footer className="border-t border-neutral-800 py-6 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
          <p className="text-sm text-neutral-500">
            2025 NFA. Stop scrolling. Start adventuring.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-sm text-neutral-500 hover:text-white transition-colors">
              Privacy
            </Link>
            <a href="mailto:laurence@notfuckingabout.com" className="text-sm text-neutral-500 hover:text-white transition-colors">
              Contact
            </a>
          </div>
        </div>
        <div className="text-xs text-neutral-600 text-center space-y-1">
          <p>Affiliate links support the site. Picks are never influenced by commission rates.</p>
          <p>As an Amazon Associate I earn from qualifying purchases.</p>
        </div>
      </div>
    </footer>
  );
}

function AppContent() {
  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col">
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/kit/:id" element={<SavedKit />} />
        <Route path="/routes" element={<RoutesPage />} />
        <Route path="/routes/:id" element={<RoutePage />} />
        <Route path="/how-we-pick" element={<HowWePick />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/gear" element={<GearCloset />} />
        <Route path="/best-for" element={<BestForIndex />} />
        <Route path="/best-for/:activity" element={<BestFor />} />
        <Route path="/gear/:slug" element={<Category />} />
        <Route path="/shakedown" element={<Shakedown />} />
        <Route path="/shakedown/:slug" element={<SavedShakedown />} />
        <Route path="/history" element={<History />} />
        <Route path="/compare" element={<Comparison />} />
        <Route path="/compare/:id" element={<Comparison />} />
      </Routes>
      <Footer />
      <OfflineIndicator />
      <InstallPrompt />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
